import { D3DragEvent } from "d3"
import { TimeSeriesPageManager, TimeSeriesPageManagerConfig } from "../../Data/TimeSeriesPageManager"
import { Page } from "../../Data/Page"
import { allWindowsDispatch, linkedWindowsDispatch } from "../../Atoms/Visualizations"
import { ReactCallbacks } from "../../Types/ReactCallbacks"
import { MIN_WINDOW_TIME_MS } from "../D3/D3UTCAxis"
import { debounce, throttle } from "lodash"
import { TimeBasedVisualizationConfig, TimelineNavigationOptions } from "../../Types/TimeBasedVisualizationConfig"
import { D3VisualizationRenderer } from "./D3VisualizationRenderer"
import { TraceDataConfig } from "../../Types/Trace"
import { WINDOW_TIME_PRESETS } from "../../../../../../Managers/VisualizationManager/Viewport/Components/XAxis"
import { ModalityDataSource } from "../../Types/ModalityDataSource"
import { getClampedStartAndEnd } from "./clamping"
import { LinkedWindowEventOptions, LinkedWindowEventType, LinkedWindowJumpEvent } from "../../Types/LinkedWindowInfo"
import { getTimelineControllerWindowId, isAnnotationPlaythroughController, LinkedWindowJumpType, TimelinePlayThroughCommand } from "../../Atoms/Timeline"
import { CheckRecordingInfoResponse } from "../../Hooks/useCheckRecordingInfo"

type GoToEndOptions = {
	waitForTimelinesToStopPlaying: boolean
}

export abstract class D3TimeBasedVisualization<
	Config extends TimeBasedVisualizationConfig,
	Callbacks extends ReactCallbacks<Config>,
	Renderer extends D3VisualizationRenderer<any, any>,
	PageManager extends TimeSeriesPageManager<any, any>
> {
	public root: HTMLDivElement
	public config: Config
	public reactCallbacks: Callbacks
	public timeSeriesPageManager: PageManager

	protected renderer?: Renderer

	private previousUpdateTimestamp: number | null = null
	private latestSyncTime: number | null = null
	private animationFrameId: number | null = null
	private recoveryOffset: number = 0
	private playThroughContext: { commandId: number; annotationEndTime: number } | null = null
	private pendingPlayThroughStart: boolean = false
	private pendingPlayThroughTimeout: ReturnType<typeof setTimeout> | null = null

	constructor(root: HTMLDivElement, config: Config, pageManager: PageManager, reactCallbacks: Callbacks) {
		this.root = root
		this.config = config
		this.reactCallbacks = reactCallbacks
		this.timeSeriesPageManager = pageManager

		// If the patient is live, get up to sync smoothly without waiting for the first sync from the server
		if (config.patientIsAdmitted) {
			this.latestSyncTime = new Date(Date.now()).getTime() - config.environmentVariables.LIVE_REVIEW_REAL_TIME_DELAY_MS
			this.animationFrameId = requestAnimationFrame(this.realTimeUpdateFrame)
		}
	}

	public abstract getVisibleTraces(): TraceDataConfig[]

	protected abstract renderPage(page: Page<any>): void
	protected abstract getModalityDataSources(): ModalityDataSource[]
	protected abstract updateDerivedState(): void
	protected hasNoRequestableData(): boolean {
		return false
	}

	// PUBLIC

	public render() {
		this.renderer?.render()
	}

	public liveEndDateUpdated(currentTime: number) {
		currentTime -= this.config.environmentVariables.LIVE_REVIEW_REAL_TIME_DELAY_MS

		if (this.animationFrameId !== null) {
			cancelAnimationFrame(this.animationFrameId)
		}

		const outOfSyncMilliseconds = currentTime - this.config.fileScale.domain()[1].getTime()

		if (outOfSyncMilliseconds < 0.1) {
			this.recoveryOffset = 0
		} else {
			this.recoveryOffset = Math.max(-2, Math.min(2, outOfSyncMilliseconds))
		}

		this.latestSyncTime = currentTime
		this.animationFrameId = requestAnimationFrame(this.realTimeUpdateFrame)
		this.timeSeriesPageManager.onEndDateUpdated(currentTime + this.config.environmentVariables.LIVE_REVIEW_READ_AHEAD_BUFFER_MS)
	}

	public updateConfig(config: Config) {
		this.config = config
		this.updateDerivedState()
		this.updatePageManager()
		this.updateWindowLoadingState()
		this.render()
	}

	protected updatePageManager() {
		this.timeSeriesPageManager.update(this.getPageManagerConfig(), this.onPageLoaded, this.onPageUnloaded, this.onPageLoadStart)
	}

	public unmount() {
		this.renderer?.timeline?.destroy()
		this.renderer?.timeline?.stop()
		this.timeSeriesPageManager.cancel()
		this.timeSeriesPageManager.clearQueue()

		if (this.animationFrameId !== null) {
			cancelAnimationFrame(this.animationFrameId)
		}

		linkedWindowsDispatch.on(`update.${this.config.id}`, null)
		allWindowsDispatch.on(`jump.${this.config.id}`, null)
		this.reactCallbacks.setTimelineViewRange(this.config.id, null)
		this.clearPlayThroughState()
	}

	public clearDataAndReload = () => {
		this.timeSeriesPageManager.resetPages()
		this.updateConfig(this.config) // this looks a little silly, but it's needed because of d3 scale issues.
		this.timeSeriesPageManager.clearQueueAndLoad()
	}

	public clearCacheAndRedraw = () => {
		this.timeSeriesPageManager.clearRenderCacheForAllPages()
	}

	public goToStart = () => {
		if (this.timelineNavigationLocked()) {
			return
		}

		const { viewDuration } = this.getStartTimeEndTimeViewDuration()
		const [recordingStartDate,] = this.config.fileScale.domain()

		let newStart = recordingStartDate.getTime()
		let newEnd = newStart + viewDuration

		const { start, end } = getClampedStartAndEnd(newStart, newEnd, this.config.fileScale)

		this.viewTimesChanged(start, end)
		this.timeSeriesPageManager.clearQueueAndLoad()
		this.updateLinkedWindows({ autoScale: true, controllerIsLinked: this.config.isLinked })
	}

	public goToEnd = (options?: GoToEndOptions) => {
		if (this.timelineNavigationLocked()) {
			return
		}

		const { viewDuration } = this.getStartTimeEndTimeViewDuration()
		const newEnd = this.config.fileScale.domain()[1].getTime()
		const newStart = newEnd - viewDuration

		const finishUpdate = () => {
			this.viewTimesChanged(newStart, newEnd)
			this.timeSeriesPageManager.clearQueueAndLoad()
			this.updateLinkedWindows({ autoScale: true, controllerIsLinked: this.config.isLinked })
		}

		// If we don't wait for any timelines to stop playing, they'll set the time back to what it was before the jump.
		// This is only a problem when jumping to the end.
		if (options?.waitForTimelinesToStopPlaying || options?.waitForTimelinesToStopPlaying === undefined) {
			setTimeout(finishUpdate, 100)
		} else {
			finishUpdate()
		}
	}

	public goToPreviousPage = () => {
		if (this.timelineNavigationLocked()) {
			return
		}

		const { startTime, viewDuration } = this.getStartTimeEndTimeViewDuration()
		const [recordingStartDate, recordingEndDate] = this.config.fileScale.domain()
		const recordingStartTime = recordingStartDate.getTime()
		const recordingEndTime = recordingEndDate.getTime()
		const totalRecordingTime = recordingEndTime - recordingStartTime

		let newStart = startTime - viewDuration
		let newEnd = newStart + viewDuration

		if (totalRecordingTime < viewDuration && newStart < recordingStartTime) {
			newEnd = recordingEndTime
			newStart = newEnd - viewDuration
		} else if (totalRecordingTime >= viewDuration && newStart < recordingStartTime) {
			newStart = recordingStartTime
			newEnd = newStart + viewDuration
		}

		this.viewTimesChanged(newStart, newEnd)
		this.timeSeriesPageManager.clearQueueAndLoad()
		this.autoScale()

		this.updateLinkedWindows({ autoScale: true, controllerIsLinked: this.config.isLinked })
	}

	public goToNextPage = (options?: TimelineNavigationOptions) => {
		if (this.timelineNavigationLocked(options)) {
			return
		}

		const { endTime, viewDuration } = this.getStartTimeEndTimeViewDuration()
		const newEnd = Math.min(this.config.fileScale.domain()[1].getTime(), endTime + viewDuration)
		const newStart = newEnd - viewDuration
		this.viewTimesChanged(newStart, newEnd)
		this.timeSeriesPageManager.clearQueueAndLoad()
		this.autoScale()
		this.updateLinkedWindows({ autoScale: true, controllerIsLinked: this.config.isLinked })
	}

	// To be overridden and implemented by children
	public autoScale = () => {}

	public zoomIn = () => {
		const { startTime, viewDuration } = this.getStartTimeEndTimeViewDuration()
		const windowSizeOptionsLargeToSmall = [...this.getZoomOptions().filter(time => time > 0)].sort((a, b) => b - a)
		const nextSmallestWindowSize = windowSizeOptionsLargeToSmall.find(time => time < viewDuration) ?? windowSizeOptionsLargeToSmall[windowSizeOptionsLargeToSmall.length - 1]
		const zoomDelta = (viewDuration - nextSmallestWindowSize) / 2
		const newStart = Math.min(this.config.fileScale.domain()[1].getTime() - MIN_WINDOW_TIME_MS, startTime + zoomDelta)
		const newEnd = Math.max(newStart + MIN_WINDOW_TIME_MS, newStart + nextSmallestWindowSize)

		const { start, end } = getClampedStartAndEnd(newStart, newEnd, this.config.fileScale)

		// The React state update will handle everything for us.
		this.reactCallbacks.setRootConfig(previous => ({ ...previous, viewScale: this.config.viewScale.domain([start, end]) }))
	}

	public zoomOut = () => {
		const { startTime, endTime, viewDuration } = this.getStartTimeEndTimeViewDuration()
		const windowSizeOptionsSmallToLarge = [...this.getZoomOptions().filter(time => time > 0)].sort((a, b) => a - b)
		const nextBiggestWindowSize = windowSizeOptionsSmallToLarge.find(preset => preset > viewDuration)

		if (!nextBiggestWindowSize) {
			return
		}
		
		const zoomDelta = (nextBiggestWindowSize - viewDuration) / 2
		const { start, end } = getClampedStartAndEnd(startTime - zoomDelta, endTime + zoomDelta, this.config.fileScale) 

		// The React state update will handle everything for us.
		this.reactCallbacks.setRootConfig(previous => ({ ...previous, viewScale: this.config.viewScale.domain([start, end]) }))
	}

	public playPause = () => {
		this.renderer?.timeline?.playPause()
	}

	public play = (takeManualControl: boolean = true) => {
		this.renderer?.timeline?.play(takeManualControl)
	}

	public pause = () => {
		this.renderer?.timeline?.pause()

		if (this.playThroughContext || this.pendingPlayThroughStart) {
			this.clearPlayThroughState()
		}
	}

	public isPlaying = () => {
		return this.renderer?.timeline?.isPlaying() ?? false
	}

	public handlePlayThroughCommand(command: TimelinePlayThroughCommand): boolean {
		const { annotationStart, annotationEnd } = command.payload
		const targetEndTime = Math.max(annotationStart, annotationEnd)
		const { viewDuration } = this.getStartTimeEndTimeViewDuration()

		if (viewDuration <= 0) {
			return false
		}

		if (!this.canControlPlayback()) {
			return false
		}

		let newStart: number
		let newEnd: number

		if (this.config.linkedWindowJumpType === LinkedWindowEventType.JUMP_MIDDLE) {
			newStart = annotationStart - viewDuration / 2
			newEnd = annotationStart + viewDuration / 2
		} else {
			newEnd = annotationStart
			newStart = newEnd - viewDuration
		}
		const { start, end } = getClampedStartAndEnd(newStart, newEnd, this.config.fileScale)

		this.viewTimesChanged(start, end)
		this.updateLinkedWindows({ autoScale: true, controllerIsLinked: this.config.isLinked })

		this.playThroughContext = { commandId: command.id, annotationEndTime: targetEndTime }
		this.renderer?.timeline?.setAnnotationPlaythroughActive(true)
		this.reactCallbacks.setLinkedWindowsTimelineController({
			windowId: this.config.id,
			mode: "annotationPlaythrough",
			requestId: command.requestId ?? command.id,
		})

		const pagesInView = this.timeSeriesPageManager.getPagesInView()
		const allLoaded = pagesInView.every(p => p?.loaded)

		if (!allLoaded) {
			this.pendingPlayThroughStart = true
			this.pendingPlayThroughTimeout = setTimeout(() => {
				this.clearPlayThroughState()
			}, 5000)
			return true
		}

		this.renderer?.timeline?.playAnnotationPlaythrough()

		if (!this.isPlaying()) {
			this.clearPlayThroughState()
			return false
		}

		return true
	}

	public updateLinkedWindows = (options?: LinkedWindowEventOptions) => {
		if (this.config.isLinked) {
			const [startDate, endDate] = this.config.viewScale.domain()
			const jumpType: LinkedWindowJumpType = this.config.linkedWindowJumpType
			const jumpDate = jumpType === LinkedWindowEventType.JUMP_MIDDLE
				? new Date((startDate.getTime() + endDate.getTime()) / 2)
				: endDate
			const event: LinkedWindowJumpEvent = { type: jumpType, date: jumpDate, controller: this.config.id, options }
			linkedWindowsDispatch.call("update", undefined, event)
		}
	}

	// every window has to implement this but not every window has a last hovered date
	public getLastHoveredDate(): Date | undefined {
		return undefined
	}

	public getDateFromClick(_event: MouseEvent): Date | undefined {
		return this.getLastHoveredDate()
	}

	public onPan = (event: D3DragEvent<SVGRectElement, any, any>) => {
		const [startDate, endDate] = this.config.viewScale.domain()

		const startTime = startDate.getTime()
		const endTime = endDate.getTime()
		const width = this.config.viewScale.range()[1]
		const offsetTime = (-event.dx * (endTime - startTime)) / width

		let newStartTime = startTime + offsetTime
		let newEndTime = endTime + offsetTime

		const { start, end } = getClampedStartAndEnd(newStartTime, newEndTime, this.config.fileScale)

		this.config.viewScale.domain([start, end])
		this.timeSeriesPageManager.requestLoad()

		requestAnimationFrame(() => {
			this.viewTimesChanged(start, end)
			this.updateLinkedWindows()
		})
	}

	public getPageManagerConfig = (): TimeSeriesPageManagerConfig => {
		return {
			patientId: this.config.patientId,
			windowId: this.config.id,
			viewScale: this.config.viewScale,
			fileScale: this.config.fileScale,
			modalityDataSources: this.getModalityDataSources(),
			timeZone: this.config.timeZone,
			patientIsAdmitted: this.config.patientIsAdmitted,
			environmentVariables: this.config.environmentVariables,
			liveModeEnabled: this.config.liveModeEnabled,
		}
	}

	public getStartTimeEndTimeViewDuration = () => {
		const [start, end] = this.config.viewScale.domain()
		const startTime = start.getTime()
		const endTime = end.getTime()
		const viewDuration = endTime - startTime
		return { startTime, endTime, viewDuration }
	}

	public onWheel = (event: WheelEvent) => {
		if (this.config.liveModeEnabled) {
			return
		}

		const [startDate, endDate] = this.config.viewScale.domain()
		const startTime = startDate.getTime()
		const endTime = endDate.getTime()
		const offset = ((-1 * event.deltaY) / 500) * (endTime - startTime)

		let newStartTime = startTime + offset
		let newEndTime = endTime + offset

		this.timeSeriesPageManager.requestLoad()
		this.debounceOnWheelStopped() // it's a debounced function so it only gets called after we stop wheeling.

		const { start, end } = getClampedStartAndEnd(newStartTime, newEndTime, this.config.fileScale)

		requestAnimationFrame(() => {
			this.viewTimesChanged(start, end)
			this.updateLinkedWindows()
		})
	}

	public debounceOnWheelStopped = debounce(() => {
		this.onWheelStopped()
	}, 100)

	public onWheelStopped = () => {
		this.timeSeriesPageManager.clearQueueAndLoad()
		this.autoScale()
		this.updateLinkedWindows({ autoScale: true, controllerIsLinked: this.config.isLinked })
	}

	// PROTECTED

	protected getZoomOptions = (): number[] => WINDOW_TIME_PRESETS.map(preset => preset.time)

	protected mount(renderer: Renderer) {
		this.renderer = renderer

		linkedWindowsDispatch.on(`update.${this.config.id}`, this.onLinkedWindowsUpdate)
		allWindowsDispatch.on(`jump.${this.config.id}`, this.onJumpToTime.bind(this))

		this.updateDerivedState()
		this.updatePageManager()
	}

	protected anotherLinkedWindowIsPlaying = () => {
		const timelineControllerWindowId = getTimelineControllerWindowId(this.config.timelineController)
		return timelineControllerWindowId !== null && this.config.isLinked && timelineControllerWindowId !== this.config.id
	}

	private timelineNavigationLocked = (options?: TimelineNavigationOptions) => {
		if (this.config.liveModeEnabled) {
			return true
		}

		if (!isAnnotationPlaythroughController(this.config.timelineController)) {
			return false
		}

		return options?.source !== "playback"
	}

	private canControlPlayback = () => {
		const timelineControllerWindowId = getTimelineControllerWindowId(this.config.timelineController)
		return !this.config.liveModeEnabled && (!this.config.isLinked || timelineControllerWindowId === null || timelineControllerWindowId === this.config.id)
	}

	private clampPlayThroughRange(startTime: number, endTime: number) {
		if (!this.playThroughContext || this.pendingPlayThroughStart) {
			return { startTime, endTime }
		}

		const { annotationEndTime } = this.playThroughContext
		const { endTime: currentEndTime } = this.getStartTimeEndTimeViewDuration()
		if (currentEndTime >= annotationEndTime) {
			return { startTime, endTime }
		}

		if (endTime <= annotationEndTime) {
			return { startTime, endTime }
		}

		const duration = endTime - startTime
		if (duration <= 0) {
			return { startTime: annotationEndTime, endTime: annotationEndTime }
		}

		const { start, end } = getClampedStartAndEnd(annotationEndTime - duration, annotationEndTime, this.config.fileScale)
		return { startTime: start, endTime: end }
	}

	private applyViewTimesChanged = (startTime: number, endTime: number): boolean => {
		const clampedPlayThroughRange = this.clampPlayThroughRange(startTime, endTime)
		startTime = clampedPlayThroughRange.startTime
		endTime = clampedPlayThroughRange.endTime

		const { viewDuration } = this.getStartTimeEndTimeViewDuration()
		const calculatedViewDuration = endTime - startTime

		if (viewDuration !== calculatedViewDuration) {
			// If the view duration has changed, we need to let React handle the update.
			// Otherwise, we end up reverting the duration back to the old duration.
			// This bug is caused by the updates from another component's timeline when it is playing.
			if (this.anotherLinkedWindowIsPlaying()) {
				return false
			}
		}

		this.config.viewScale.domain([startTime, endTime])
		this.renderer?.viewTimesChanged()
		if (this.shouldReloadPages()) {
			this.timeSeriesPageManager?.clearQueueAndLoad()
		}
		this.updateWindowLoadingState()
		this.reactCallbacks.setTimelineViewRange(this.config.id, { start: startTime, end: endTime })
		this.handlePlayThroughProgress(endTime)
		return true
	}

	protected shouldReloadPages(): boolean {
		return true
	}

	public viewTimesChanged = (startTime: number, endTime: number) => {
		this.applyViewTimesChanged(startTime, endTime)
	}

	public onTimelineSliderDrag = () => {
		this.timeSeriesPageManager?.clearQueueAndLoad()
		this.renderer?.onTimelineSliderDrag()
		this.updateLinkedWindows()
	}

	public onTimelineSliderDragEnd = () => {
		this.timeSeriesPageManager.clearQueueAndLoad()
		this.updateLinkedWindows({ autoScale: true, controllerIsLinked: this.config.isLinked })
	}

	private onLinkedWindowsUpdate = (event: LinkedWindowJumpEvent) => {
		if (event.controller === this.config.id) {
			// Self-emitted events can go through if "jumpSelf" is enabled, even if the window is unlinked.
			if (!event.options?.jumpSelf)  {
				return
			}
		} else {
			// Events from other windows require the consuming window to be linked
			if (!this.config.isLinked) {
				return
			}

			// Events from other windows are only consumed if the controller is linked
			if (event.options && !event.options.controllerIsLinked) {
				return
			}
		}

		this.onJumpToTime(event)
	}

	protected onJumpToTime = (event: LinkedWindowJumpEvent) => {
		if (this.playThroughContext || this.pendingPlayThroughStart) {
			return
		}

		// Filter by allowed window types
		if (event.options?.windowTypes && !event.options.windowTypes.includes(this.config.windowType)) {
			return
		}

		const { viewDuration } = this.getStartTimeEndTimeViewDuration()
		const time = event.date.getTime()
		let clamped: { start: number, end: number }

		switch(event.type) {
			case LinkedWindowEventType.JUMP_END:
				clamped = getClampedStartAndEnd(time - viewDuration, time, this.config.fileScale)
				break
			case LinkedWindowEventType.JUMP_MIDDLE:
				clamped = getClampedStartAndEnd(time - viewDuration / 2, time + viewDuration / 2, this.config.fileScale)
				break
			case LinkedWindowEventType.JUMP_START:
				clamped = getClampedStartAndEnd(time, time + viewDuration, this.config.fileScale)
				break
			default:
				throw new Error(`Invalid LinkedWindowEventType`)
		}

		requestAnimationFrame(() => {
			this.viewTimesChanged(clamped.start, clamped.end)

			if (event.options?.autoScale) {
				this.autoScale()
			}
		})
	}

	protected onPageLoadStart = (page: Page<any>) => {
		this.updateWindowLoadingState()
	}

	private handlePlayThroughProgress(endTime: number) {
		if (!this.playThroughContext) {
			return
		}

		if (this.pendingPlayThroughStart) {
			return
		}

		const { annotationEndTime } = this.playThroughContext

		if (!this.isPlaying()) {
			this.clearPlayThroughState()
			return
		}

		if (endTime >= annotationEndTime) {
			this.playThroughContext = null
			this.renderer?.timeline?.setAnnotationPlaythroughActive(false)
			this.pause()
		}
	}

	private clearPlayThroughState() {
		const playThroughCommandId = this.playThroughContext?.commandId
		this.playThroughContext = null
		this.pendingPlayThroughStart = false
		this.renderer?.timeline?.setAnnotationPlaythroughActive(false)
		if (this.pendingPlayThroughTimeout !== null) {
			clearTimeout(this.pendingPlayThroughTimeout)
			this.pendingPlayThroughTimeout = null
		}
		this.reactCallbacks.setLinkedWindowsTimelineController(previous => {
			if (
				isAnnotationPlaythroughController(previous)
				&& previous.windowId === this.config.id
				&& previous.requestId === playThroughCommandId
			) {
				return null
			}
			return previous
		})
	}

	protected onPageLoaded = (page: Page<any>) => {
		const pagesInView = this.timeSeriesPageManager.getPagesInView()
		this.renderer?.timeline?.updateNotLoadedRegions()

		if (pagesInView.includes(page)) {
			this.renderPage(page)
			this.updateWindowLoadingState()
		}

		if (this.pendingPlayThroughStart && this.playThroughContext) {
			const allLoaded = pagesInView.every(p => p?.loaded)
			if (allLoaded) {
				this.pendingPlayThroughStart = false
				if (this.pendingPlayThroughTimeout !== null) {
					clearTimeout(this.pendingPlayThroughTimeout)
					this.pendingPlayThroughTimeout = null
				}
				this.renderer?.timeline?.playAnnotationPlaythrough()

				if (!this.isPlaying()) {
					this.clearPlayThroughState()
				}
			}
		}
	}

	protected onPageUnloaded = (page: Page<any>) => {
		this.renderer?.timeline?.updateNotLoadedRegions()
	}

	protected updateWindowLoadingState = throttle(() => {
		if (this.hasNoRequestableData()) {
			this.reactCallbacks.updateWindowLoadingProgress(0, 0, undefined, true)
			return
		}

		const maxViewportTime = this.config.viewScale.domain()[1].getTime()
		let progress = 0
		let total = 0
		let error = undefined
		let isViewportDataLoaded = true

		this.timeSeriesPageManager.getPagesInView().forEach((page: Page<any>) => {
			if (page.receiveDataError) {
				error = page.receiveDataError
			}

			// Prevent a window from saying it's fully loaded if it has not requested data yet.
			if ([...page.responseBuffers.values()].length === 0 && page.startTime < maxViewportTime) {
				isViewportDataLoaded = false
				total += 1
			}

			page.responseBuffers.forEach(buffer => {
				progress += buffer.nextId - 1
				total += buffer.maxId
				
				if (!buffer.isLoadedUpTo(Math.min(maxViewportTime, page.endTime))) {
					isViewportDataLoaded = false
				}
			})
		})

		// If loading is happening outside the viewport, it doesn't matter. Just show that the data is "Ready".
		if (isViewportDataLoaded) {
			progress = total
		}

		this.reactCallbacks.updateWindowLoadingProgress(progress, total, error)
	}, 200)

	private realTimeUpdateFrame = (timestamp: number) => {
		if (!this.latestSyncTime) {
			return
		}

		// If this is the first animation frame, jump to the latest timestamp
		if (!this.previousUpdateTimestamp) {
			const [start] = this.config.fileScale.domain()
			this.config.fileScale.domain([start, this.latestSyncTime])
			this.previousUpdateTimestamp = timestamp
		}

		// Calculate the amount of time it took since the last frame
		let deltaTime = timestamp - this.previousUpdateTimestamp

		// If we get ahead or behind, we can speed up or slow down to smoothly get back on track.
		deltaTime += this.recoveryOffset

		const [start, end] = this.config.fileScale.domain()
		const newEnd = end.getTime() + deltaTime

		// Update the file scale and re-render
		this.config.fileScale.domain([start, newEnd])
		this.renderer?.liveEndDateUpdated()
		this.previousUpdateTimestamp = timestamp
		this.animationFrameId = requestAnimationFrame(this.realTimeUpdateFrame)

		if (this.config.liveModeEnabled) {
			const [viewStart, viewEnd] = this.config.viewScale.domain()
			this.viewTimesChanged(newEnd - (viewEnd.getTime() - viewStart.getTime()), newEnd)
		}
	}
	
	public updateRecordingInfo(info: CheckRecordingInfoResponse) {
		// If the patient has been discharged, use the data to appropriately transition to retrospective review.
		if (!info.isAdmitted) {
			if (this.animationFrameId) {
				cancelAnimationFrame(this.animationFrameId)
			}

			// Update the recording start and end times
			this.config.fileScale.domain([info.startTime, info.endTime])
			
			// Move the window to the end of the file
			if (this.config.liveModeEnabled) {
				const [viewStart, viewEnd] = this.config.viewScale.domain()
				const newEndTime = info.endTime
				this.viewTimesChanged(newEndTime - (viewEnd.getTime() - viewStart.getTime()), newEndTime)
			}

			// Disable live mode, re-link all displays
			this.reactCallbacks.setRootConfig(previous => ({...previous, liveModeEnabled: false, isLinked: true}))
		}
	}
}
